import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { tipApi } from '@/services/learningManagementApi';
import { usePaginatedList, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Eye, Edit, Trash2, Lightbulb, Search } from 'lucide-react';
import { LoadingState, EmptyState } from '@/components/common';
import { formatDate } from '@/utils/dateFormatter';
import type { Tip, TipStatus } from '@/types/learningManagement';

export const TipListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterDifficulty, setFilterDifficulty] = useState<string>('all');
  const { hasPermission } = useAdminAuth();

  const canCreate = hasPermission('create', 'tip');
  const canEdit = hasPermission('edit', 'tip');
  const canDelete = hasPermission('delete', 'tip');

  // Build fetch function with conditional API calls
  const fetchTipsFn = useMemo(() => {
    return async (params: { page: number; size: number }) => {
      if (searchQuery.trim()) {
        return tipApi.search({
          query: searchQuery,
          page: params.page,
          size: params.size,
        });
      } else if (filterCategory !== 'all') {
        return tipApi.listByCategory(filterCategory, { page: params.page, size: params.size });
      } else if (filterDifficulty !== 'all') {
        return tipApi.listByDifficulty(filterDifficulty, { page: params.page, size: params.size });
      } else {
        return tipApi.list({ page: params.page, size: params.size, sortBy: 'createdAt', sortDirection: 'desc' });
      }
    };
  }, [searchQuery, filterCategory, filterDifficulty]);

  // Use paginated list hook
  const {
    data: tips,
    isLoading,
    error,
    pagination,
    setPage,
    setSize: _setSize,
    refetch,
  } = usePaginatedList<Tip>({
    fetchFn: fetchTipsFn,
    entityName: 'tips',
    suppress404Errors: true,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (tipId: string) => {
      await tipApi.delete(tipId);
    },
    entityName: 'tip',
    operation: 'delete',
    onSuccess: () => {
      refetch();
    },
  });

  const handleDelete = async (tipId: string) => {
    if (!window.confirm('Are you sure you want to delete this tip?')) return;
    await deleteMutation.mutate(tipId);
  };

  const handleSearch = () => {
    setPage(0);
    refetch();
  };

  const getStatusColor = (status: TipStatus) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'DRAFT':
      case 'SCHEDULED':
      case 'IN_PROGRESS':
      case 'NOT_STARTED':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'PAUSED':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'ARCHIVED':
      case 'EXPIRED':
      case 'COMPLETED':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const tipId = (tip: Tip) => tip.tipId || tip.id || '';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Tip Management</h1>
          <p className="text-muted-foreground mt-2">Manage all tips</p>
        </div>
        {canCreate && (
          <Button asChild>
            <Link to="/admin/tips/new">
              <Plus className="mr-2 h-4 w-4" />
              Create Tip
            </Link>
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters & Search</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <div className="md:col-span-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Search tips..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="DAILY_TIP">Daily Tip</SelectItem>
                <SelectItem value="WEEKLY_TIP">Weekly Tip</SelectItem>
                <SelectItem value="MONTHLY_TIP">Monthly Tip</SelectItem>
                <SelectItem value="QUICK_ADVICE">Quick Advice</SelectItem>
                <SelectItem value="IN_DEPTH_GUIDE">In-Depth Guide</SelectItem>
                <SelectItem value="CHECKLIST">Checklist</SelectItem>
                <SelectItem value="BEST_PRACTICES">Best Practices</SelectItem>
                <SelectItem value="TIPS_AND_TRICKS">Tips and Tricks</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterDifficulty} onValueChange={setFilterDifficulty}>
              <SelectTrigger>
                <SelectValue placeholder="All Difficulties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Difficulties</SelectItem>
                <SelectItem value="EASY">Easy</SelectItem>
                <SelectItem value="MODERATE">Moderate</SelectItem>
                <SelectItem value="CHALLENGING">Challenging</SelectItem>
                <SelectItem value="ADVANCED">Advanced</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tips Table */}
      <Card>
        <CardHeader>
          <CardTitle>Tips ({pagination.totalElements})</CardTitle>
          <CardDescription>List of all tips</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingState message="Loading tips..." />
          ) : (error || tips.length === 0) ? (
            <EmptyState
              title="No data found"
              description={error ? "Unable to load tips. Please try again or create a new one." : "Get started by creating your first tip."}
              icon={Lightbulb}
              actions={[
                ...(canCreate ? [{
                  label: 'Create a new one',
                  onClick: () => navigate('/admin/tips/new'),
                  variant: 'default' as const,
                }] : []),
                {
                  label: 'Try again',
                  onClick: () => refetch(),
                  variant: 'outline' as const,
                },
              ]}
              searchQuery={searchQuery.trim() || undefined}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Difficulty</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Generated</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tips.map((tip) => (
                    <TableRow key={tipId(tip)}>
                      <TableCell className="font-medium">{tip.title}</TableCell>
                      <TableCell>{tip.category}</TableCell>
                      <TableCell>{tip.tipType}</TableCell>
                      <TableCell>{tip.difficultyLevel}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(tip.status)}`}>
                          {tip.status}
                        </span>
                      </TableCell>
                      <TableCell>{tip.tipGenerated ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        {formatDate(tip.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/admin/tips/${tipId(tip)}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          {canEdit && (
                            <Button variant="ghost" size="icon" asChild>
                              <Link to={`/admin/tips/${tipId(tip)}/edit`}>
                                <Edit className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(tipId(tip))}
                              disabled={deleteMutation.isLoading}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {(pagination.totalPages > 1 || pagination.totalElements > pagination.size) && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Page {pagination.page + 1} of {pagination.totalPages || 1} ({pagination.totalElements} total)
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.max(0, pagination.page - 1))}
                      disabled={pagination.page === 0 || isLoading}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.min((pagination.totalPages || 1) - 1, pagination.page + 1))}
                      disabled={pagination.page >= (pagination.totalPages || 1) - 1 || isLoading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TipListPage;
